import { gql, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import {
  CSGetSub as GET_SUB,
  EntityUser,
  EntityUserRole,
  getUser,
  User,
} from '@admiin-com/ds-graphql';
import { useUserEntities } from '../useUserEntities/useUserEntities';
import { useSelectedEntity } from '../useSelectedEntity/useSelectedEntity';

const CACHE_KEY = 'admiin_current_user';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedUser {
  user: User;
  timestamp: number;
}

const getCachedUser = (): User | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {
      return null;
    }
    const parsed: CachedUser = JSON.parse(cached);
    const now = Date.now();
    if (now - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.user;
  } catch {
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

const setCachedUser = (user: User): void => {
  try {
    const cached: CachedUser = {
      user,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Silently fail if localStorage is not available
  }
};

export const clearCachedUser = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Silently fail if localStorage is not available
  }
};

export interface UserWithLoading extends User {
  __loading?: boolean;
}

export function useCurrentUser(): UserWithLoading {
  const cachedUser = useMemo(() => getCachedUser(), []);
  const [initialUser, setInitialUser] = useState<User | undefined>(
    cachedUser || undefined
  );

  const { data: subData, loading: subLoading } = useQuery(gql(GET_SUB));
  const userId = subData?.sub;

  const { data: userData, loading: userLoading } = useQuery(gql(getUser), {
    variables: {
      id: userId,
    },
    skip: !userId,
  });

  const user: User = useMemo(() => userData?.getUser || {}, [userData]);
  const loading = subLoading || (!!userId && userLoading);

  // Cache user data when it's loaded
  useEffect(() => {
    if (user && Object.keys(user).length > 0) {
      setCachedUser(user);
      setInitialUser(user);
    }
  }, [user]);

  // Return cached user during initial load if available
  if (loading && cachedUser && !userData?.getUser) {
    return { ...cachedUser, __loading: false } as UserWithLoading;
  }

  return { ...user, __loading: loading } as UserWithLoading;
}
export function useUserId() {
  const { data: subData } = useQuery(gql(GET_SUB));
  const userId = subData?.sub;
  return userId;
}
export function useOwnerPermission() {
  const userId = useUserId();
  const { users } = useUserEntities();
  const isOwner = users?.some(
    (user) => user.userId === userId && user.role === 'OWNER'
  );
  return isOwner;
}
export const useCurrentyEntityUser = () => {
  const userId = useUserId();
  const { entity } = useSelectedEntity();
  const entityUsers = entity?.entityUsers?.items;
  return entityUsers?.find(
    (user: EntityUser | null) => user?.userId === userId
  );
};

export const useNonAccountEntityUsers = () => {
  const { users: ownerUsers } = useUserEntities({
    role: EntityUserRole.OWNER,
  });
  const { users: adminUsers } = useUserEntities({
    role: EntityUserRole.ADMIN,
  });
  const entityUsers = useMemo(
    () => [...new Set([...ownerUsers, ...adminUsers])],
    [ownerUsers, adminUsers]
  );
  return {
    entityUsers,
  };
};
